import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import LoadingOverlay from 'react-loading-overlay';
import './GutAssessmentQuestion.scss';
import gutAssessmentImages from '../gutAssessmentImages';
import { commonConstant } from '../../../common';
import { fetchGutAssessmentQuestions, submitGutAssessmentAnswers } from '../gutAssessment.service';

const GutAssessmentQuestion = () => {
  const history = useHistory();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const assessmentId = params.get('assessment_id') || '';

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedChoice, setSelectedChoice] = useState(null);

  const stateImages = [
    gutAssessmentImages.questionState1,
    gutAssessmentImages.questionState2,
    gutAssessmentImages.questionState3,
    gutAssessmentImages.questionState4,
    gutAssessmentImages.questionState5,
  ];

  useEffect(() => {
    const wrapper = document.getElementById('wrapper');
    if (wrapper) wrapper.style.maxWidth = '100%';
    return () => {
      if (wrapper) wrapper.style.maxWidth = '';
    };
  }, []);

  useEffect(() => {
    if (assessmentId) {
      setLoading(true);
      fetchGutAssessmentQuestions(assessmentId)
        .then((data) => {
          const sorted = (data?.hw_assessments_questions || []).sort(
            (a, b) => (a.display_order || 0) - (b.display_order || 0)
          );
          setQuestions(sorted);
          setGroups(data?.metaData?.groups || []);
        })
        .catch((err) => console.error('Fetch questions error:', err))
        .finally(() => setLoading(false));
    }
  }, [assessmentId]);

  if (loading) {
    return (
      <div className="gut-question" data-testid="gut-question-loading">
        <LoadingOverlay
          text="Loading"
          spinner
          active={loading}
          styles={{ wrapper: { width: '100%', height: '100%', position: 'fixed', top: 0, left: 0, zIndex: 10 } }}
        >
          <div />
        </LoadingOverlay>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="gut-question" data-testid="gut-question-empty">
        <p>ไม่พบคำถาม</p>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  // Determine which group the current question belongs to
  const getActiveGroupIndex = () => {
    const questionId = currentQuestion?._id;
    for (let i = 0; i < groups.length; i++) {
      if (groups[i].question_ids && groups[i].question_ids.includes(questionId)) {
        return i;
      }
    }
    return 0;
  };

  const categoryIndex = Math.min(getActiveGroupIndex(), stateImages.length - 1);

  const handleChoiceSelect = (choice) => {
    setSelectedChoice(choice._id);
    setAnswers({
      ...answers,
      [currentQuestion._id]: {
        questionId: currentQuestion._id,
        answerId: choice._id,
        score: choice.score,
      },
    });

    setTimeout(() => {
      if (currentIndex < totalQuestions - 1) {
        setCurrentIndex(currentIndex + 1);
        setSelectedChoice(null);
      } else {
        handleSubmit({
          ...answers,
          [currentQuestion._id]: {
            questionId: currentQuestion._id,
            answerId: choice._id,
            score: choice.score,
          },
        });
      }
    }, 300);
  };

  const handleSubmit = async (finalAnswers) => {
    try {
      setLoading(true);
      const answersArray = Object.values(finalAnswers);
      const result = await submitGutAssessmentAnswers({
        assessmentId,
        answers: answersArray,
      });
      if (result?.success) {
        history.push(
          `${commonConstant.pathGutAssessmentResult}?assessment_id=${assessmentId}&answer_id=${result.answerId}`
        );
      }
    } catch (err) {
      console.error('Submit error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setSelectedChoice(null);
    } else {
      history.goBack();
    }
  };

  const progress = ((currentIndex + 1) / totalQuestions) * 100;

  return (
    <div className="gut-question" data-testid="gut-question-page" style={{ backgroundImage: `url(${gutAssessmentImages.questionBackground})`, backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat' }}>
      <div className="gut-question__container" data-testid="gut-question-container">
        <img className="gut-question__header" data-testid="gut-question-header" src={gutAssessmentImages.questionHeader} alt="header" />

        <div className='gut-question__state-container' data-testid="gut-question-state-container">
          <img
            className="gut-question__state"
            data-testid="gut-question-state-image"
            src={stateImages[categoryIndex]}
            alt={`state ${categoryIndex + 1}`}
          />
        </div>

        <div className="gut-question__question-box" data-testid="gut-question-box">
          <div className="gut-question__question-title" data-testid="gut-question-title">คำถามที่ {currentIndex + 1}</div>
          <div className="gut-question__question-text" data-testid="gut-question-text">{currentQuestion.question}</div>
        </div>

        <div className="gut-question__choices" data-testid="gut-question-choices">
          {currentQuestion.question_value?.map((choice) => (
            <div
              key={choice._id}
              className={`gut-question__choice ${selectedChoice === choice._id ? 'gut-question__choice--selected' : ''}`}
              data-testid={`gut-question-choice-${choice._id}`}
              onClick={() => handleChoiceSelect(choice)}
            >
              {choice.answer || choice.label}
            </div>
          ))}
        </div>

        <div className="gut-question__progress" data-testid="gut-question-progress">
          <div className="gut-question__progress-back" data-testid="gut-question-btn-back" onClick={handleBack}>
            <img src={gutAssessmentImages.questionArrowBack} alt="back" />
          </div>
          <div className="gut-question__progress-bar" data-testid="gut-question-progress-bar">
            <div className="gut-question__progress-fill" data-testid="gut-question-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="gut-question__progress-count" data-testid="gut-question-progress-count">
            {currentIndex + 1}/{totalQuestions}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GutAssessmentQuestion;
